import { useState } from 'react'
import { useSearchTerms } from '@/api/promotions'
import DateRangePicker from '@/components/DateRangePicker'
import { useDateRange } from '@/lib/dateRange'
import { cn } from '@/lib/utils'
import { ChevronLeft, ChevronRight, ArrowUpDown } from 'lucide-react'

const categoryColors: Record<string, string> = {
  winner: 'bg-emerald-100 text-emerald-800',
  potential: 'bg-amber-100 text-amber-800',
  loser: 'bg-red-100 text-red-800',
  insufficient_data: 'bg-zinc-100 text-zinc-500',
}

const categoryLabels: Record<string, string> = {
  winner: 'Gewinner',
  potential: 'Potential',
  loser: 'Verlierer',
  insufficient_data: 'Zu wenig Daten',
}

type SortField = 'clicks' | 'orders' | 'acos' | 'spend' | 'sales' | 'roas' | 'impressions'

export default function SearchTermAnalysis() {
  const { range, setRange } = useDateRange({ fallbackDays: 7 })
  const [category, setCategory] = useState<string | undefined>()
  const [limit] = useState(50)
  const [offset, setOffset] = useState(0)
  const [sortField, setSortField] = useState<SortField>('clicks')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  const { data, isLoading } = useSearchTerms({ range, category, limit, offset })
  const terms = data?.searchTerms ?? []
  const total = data?.total ?? 0

  // Client-side sort (data is already paginated from backend)
  const sorted = [...terms].sort((a, b) => {
    const aVal = a[sortField] ?? 0
    const bVal = b[sortField] ?? 0
    return sortDir === 'desc' ? bVal - aVal : aVal - bVal
  })

  function handleSort(field: SortField) {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc')
    else { setSortField(field); setSortDir('desc') }
  }

  const page = Math.floor(offset / limit)
  const totalPages = Math.ceil(total / limit)

  function SortHeader({ field, children }: { field: SortField; children: React.ReactNode }) {
    return (
      <th className="px-3 py-2 text-right font-medium cursor-pointer select-none hover:text-[var(--foreground)]"
        onClick={() => handleSort(field)}>
        <span className="inline-flex items-center gap-1">
          {children}
          <ArrowUpDown className={cn('h-3 w-3', sortField === field ? 'text-[var(--foreground)]' : 'text-[var(--muted-foreground)]')} />
        </span>
      </th>
    )
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <DateRangePicker value={range} onChange={(r) => { setRange(r); setOffset(0) }} />

        <select value={category ?? ''} onChange={e => { setCategory(e.target.value || undefined); setOffset(0) }}
          className="h-9 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm">
          <option value="">Alle Kategorien</option>
          <option value="winner">Gewinner</option>
          <option value="potential">Potential</option>
          <option value="loser">Verlierer</option>
          <option value="insufficient_data">Zu wenig Daten</option>
        </select>

        <span className="text-sm text-[var(--muted-foreground)]">{total} Search Terms</span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-[var(--border)]">
        <table className="min-w-full text-sm">
          <thead className="border-b border-[var(--border)] bg-[var(--muted)]">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Search Term</th>
              <th className="px-3 py-2 text-left font-medium">Kategorie</th>
              <SortHeader field="clicks">Clicks</SortHeader>
              <SortHeader field="orders">Orders</SortHeader>
              <SortHeader field="acos">ACoS</SortHeader>
              <SortHeader field="spend">Spend</SortHeader>
              <SortHeader field="sales">Sales</SortHeader>
              <SortHeader field="roas">ROAS</SortHeader>
              <SortHeader field="impressions">Impr.</SortHeader>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-[var(--muted-foreground)]">Lade...</td></tr>
            )}
            {!isLoading && sorted.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-[var(--muted-foreground)]">Keine Search Terms vorhanden</td></tr>
            )}
            {sorted.map((term, idx) => (
              <tr key={`${term.customerSearchTerm}-${term.campaignId}-${idx}`} className="border-b border-[var(--border)] hover:bg-[var(--muted)]/50">
                <td className="px-3 py-2 font-mono text-xs max-w-[250px] truncate" title={term.customerSearchTerm}>
                  {term.customerSearchTerm}
                </td>
                <td className="px-3 py-2">
                  <span className={cn('inline-flex rounded-full px-2 py-0.5 text-xs font-medium', categoryColors[term.category])}>
                    {categoryLabels[term.category]}
                  </span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{term.clicks}</td>
                <td className="px-3 py-2 text-right tabular-nums">{term.orders}</td>
                <td className="px-3 py-2 text-right tabular-nums">{(term.acos * 100).toFixed(1)}%</td>
                <td className="px-3 py-2 text-right tabular-nums">{term.spend.toFixed(2)} EUR</td>
                <td className="px-3 py-2 text-right tabular-nums">{term.sales.toFixed(2)} EUR</td>
                <td className="px-3 py-2 text-right tabular-nums">{term.roas.toFixed(2)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{term.impressions}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-[var(--muted-foreground)]">
          <span>Seite {page + 1} von {totalPages}</span>
          <div className="flex gap-1">
            <button onClick={() => setOffset(Math.max(0, offset - limit))} disabled={page === 0}
              className="rounded-md border border-[var(--border)] p-1.5 disabled:opacity-30">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button onClick={() => setOffset(offset + limit)} disabled={page >= totalPages - 1}
              className="rounded-md border border-[var(--border)] p-1.5 disabled:opacity-30">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
